import { supabase } from '../supabase';
import { v4 as uuidv4 } from 'uuid';

/**
 * Serviço para verificar e corrigir a consistência das recomendações
 * Este serviço procura por problemas comuns como recomendações duplicadas, 
 * referências órfãs, e outras inconsistências no banco de dados.
 */
export class RecommendationConsistencyService {
  /**
   * Verifica e corrige problemas comuns de consistência
   * @param userId ID do usuário para verificar (opcional, se não fornecido verifica todos)
   * @returns Relatório das correções realizadas
   */
  public async verifyAndFix(userId?: string): Promise<{ 
    duplicatesRemoved: number, 
    orphansFixed: number,
    invalidFixed: number,
    total: number
  }> {
    const result = {
      duplicatesRemoved: 0,
      orphansFixed: 0,
      invalidFixed: 0,
      get total() { return this.duplicatesRemoved + this.orphansFixed + this.invalidFixed; }
    };
    
    try {
      // 1. Encontrar e remover duplicatas (mesmo user_id e game_id)
      await this.fixDuplicateRecommendations(userId).then(count => {
        result.duplicatesRemoved = count;
      });
      
      // 2. Encontrar e corrigir recomendações com game_id inválido
      await this.fixInvalidGameReferences(userId).then(count => {
        result.invalidFixed = count;
      });
      
      // 3. Encontrar e corrigir recomendações com user_id inválido
      await this.fixOrphanRecommendations(userId).then(count => {
        result.orphansFixed += count;
      });
      
      console.log(`Verificação de consistência concluída: ${result.total} problemas corrigidos`);
      return result;
    } catch (error) {
      console.error('Erro ao verificar consistência das recomendações:', error);
      return result;
    }
  }
  
  /**
   * Encontra e corrige recomendações duplicadas (mesmo user_id e game_id)
   */
  private async fixDuplicateRecommendations(userId?: string): Promise<number> {
    try {
      // Buscar recomendações agrupadas por user_id e game_id
      let query = supabase.rpc('find_duplicate_recommendations');
      
      if (userId) {
        // Não podemos filtrar na RPC, então vamos fazer manualmente
        const { data: allDuplicates, error } = await query;
        
        if (error) {
          console.error('Erro ao buscar duplicatas:', error);
          return 0;
        }
        
        // Filtrar apenas para o usuário especificado
        const userDuplicates = allDuplicates.filter((d: any) => d.user_id === userId);
        
        let count = 0;
        for (const dup of userDuplicates) {
          // Manter apenas a recomendação mais recente, excluir as demais
          const { data: recs, error: recError } = await supabase
            .from('recommendations')
            .select('id, created_at')
            .eq('user_id', dup.user_id)
            .eq('game_id', dup.game_id)
            .order('created_at', { ascending: false });
            
          if (recError || !recs || recs.length <= 1) {
            continue;
          }
          
          // Manter o primeiro (mais recente), excluir os demais
          const toDelete = recs.slice(1).map(r => r.id);
          
          if (toDelete.length > 0) {
            const { error: deleteError } = await supabase
              .from('recommendations')
              .delete()
              .in('id', toDelete);
              
            if (!deleteError) {
              count += toDelete.length;
              console.log(`Removidas ${toDelete.length} recomendações duplicadas para user_id=${dup.user_id}, game_id=${dup.game_id}`);
            }
          }
        }
        
        return count;
      } else {
        // Implementar lógica para corrigir todas as duplicatas (potencialmente grande volume)
        // Em produção, isso deve ser feito em lotes
        const { data: allDuplicates, error } = await query;
        
        if (error) {
          console.error('Erro ao buscar duplicatas:', error);
          return 0;
        }
        
        let count = 0;
        for (const dup of allDuplicates) {
          // Manter apenas a recomendação mais recente, excluir as demais
          const { data: recs, error: recError } = await supabase
            .from('recommendations')
            .select('id, created_at')
            .eq('user_id', dup.user_id)
            .eq('game_id', dup.game_id)
            .order('created_at', { ascending: false });
            
          if (recError || !recs || recs.length <= 1) {
            continue;
          }
          
          // Manter o primeiro (mais recente), excluir os demais
          const toDelete = recs.slice(1).map(r => r.id);
          
          if (toDelete.length > 0) {
            const { error: deleteError } = await supabase
              .from('recommendations')
              .delete()
              .in('id', toDelete);
              
            if (!deleteError) {
              count += toDelete.length;
              console.log(`Removidas ${toDelete.length} recomendações duplicadas para user_id=${dup.user_id}, game_id=${dup.game_id}`);
            }
          }
        }
        
        return count;
      }
    } catch (error) {
      console.error('Erro ao corrigir recomendações duplicadas:', error);
      return 0;
    }
  }
  
  /**
   * Encontra e corrige recomendações com game_id inválido
   */
  private async fixInvalidGameReferences(userId?: string): Promise<number> {
    try {
      // Busca jogos válidos para usar na correção
      const { data: validGames, error: gamesError } = await supabase
        .from('games')
        .select('id')
        .limit(1);
        
      if (gamesError || !validGames || validGames.length === 0) {
        console.error('Erro ao buscar jogo válido para correção:', gamesError);
        return 0;
      }
      
      const validGameId = validGames[0].id;
      
      // Buscar recomendações com game_id que não existe na tabela de games
      let query = supabase
        .from('recommendations')
        .select('id, user_id, game_id', { count: 'exact' });
        
      if (userId) {
        query = query.eq('user_id', userId);
      }
      
      const { data: invalidRecs, error, count } = await query;
      
      if (error) {
        console.error('Erro ao buscar recomendações com referências inválidas:', error);
        return 0;
      }
      
      if (!invalidRecs || invalidRecs.length === 0) {
        return 0; // Nada para corrigir
      }
      
      // Para cada recomendação com game_id inválido, atualizar para um ID válido
      let fixed = 0;
      for (const rec of invalidRecs) {
        // Verificar se o game_id é válido
        const { data: gameCheck, error: gameCheckError } = await supabase
          .from('games')
          .select('id')
          .eq('id', rec.game_id)
          .maybeSingle();
          
        if (gameCheckError || !gameCheck) {
          // Game_id inválido, atualizar
          const { error: updateError } = await supabase
            .from('recommendations')
            .update({ game_id: validGameId })
            .eq('id', rec.id);
            
          if (!updateError) {
            fixed++;
            console.log(`Corrigido game_id inválido na recomendação ${rec.id}`);
          }
        }
      }
      
      return fixed;
    } catch (error) {
      console.error('Erro ao corrigir referências inválidas em recomendações:', error);
      return 0;
    }
  }
  
  /**
   * Encontra e corrige recomendações órfãs (sem user_id válido)
   */
  private async fixOrphanRecommendations(userId?: string): Promise<number> {
    try {
      // Se foi especificado um userId, não precisamos verificar órfãos
      if (userId) {
        return 0;
      }
      
      // Buscar usuário válido para atribuir às recomendações órfãs
      const { data: validUsers, error: usersError } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);
        
      if (usersError || !validUsers || validUsers.length === 0) {
        console.error('Erro ao buscar usuário válido para correção:', usersError);
        return 0;
      }
      
      const validUserId = validUsers[0].id;
      
      // Buscar recomendações com user_id que não existe na tabela de profiles
      const { data: orphanRecs, error } = await supabase
        .from('recommendations')
        .select('id, user_id', { count: 'exact' });
        
      if (error) {
        console.error('Erro ao buscar recomendações órfãs:', error);
        return 0;
      }
      
      if (!orphanRecs || orphanRecs.length === 0) {
        return 0; // Nada para corrigir
      }
      
      // Para cada recomendação órfã, atualizar para um user_id válido
      let fixed = 0;
      for (const rec of orphanRecs) {
        // Verificar se o user_id é válido
        const { data: userCheck, error: userCheckError } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', rec.user_id)
          .maybeSingle();
          
        if (userCheckError || !userCheck) {
          // User_id inválido, atualizar
          const { error: updateError } = await supabase
            .from('recommendations')
            .update({ user_id: validUserId })
            .eq('id', rec.id);
            
          if (!updateError) {
            fixed++;
            console.log(`Corrigido user_id inválido na recomendação ${rec.id}`);
          }
        }
      }
      
      return fixed;
    } catch (error) {
      console.error('Erro ao corrigir recomendações órfãs:', error);
      return 0;
    }
  }
  
  /**
   * Remove recomendações obsoletas (antigas e sem feedback)
   * @param userId ID do usuário para limpar recomendações
   * @param olderThanDays Remover recomendações mais antigas que este número de dias
   */
  public async cleanupOldRecommendations(userId: string, olderThanDays: number = 30): Promise<number> {
    if (!userId) {
      console.error('ID de usuário obrigatório para limpeza de recomendações');
      return 0;
    }
    
    try {
      // Calcular data limite
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
      
      // Remover recomendações antigas sem feedback (liked é null)
      const { data, error } = await supabase
        .from('recommendations')
        .delete({ count: 'exact' })
        .eq('user_id', userId)
        .is('liked', null)
        .lt('created_at', cutoffDate.toISOString());
        
      if (error) {
        console.error('Erro ao limpar recomendações antigas:', error);
        return 0;
      }
      
      const count = data?.length || 0;
      console.log(`Removidas ${count} recomendações antigas sem feedback para o usuário ${userId}`);
      return count;
    } catch (error) {
      console.error('Erro ao limpar recomendações antigas:', error);
      return 0;
    }
  }
} 